import type { Node, Edge } from '@xyflow/react';
import type { WorkflowDefinitionResponse } from '@erp/rpc';
import {
  buildSocketExpressionsFromEdges,
  readManualExpressionOverrides,
} from '@erp/workflows';
import { v4 as uuidv4 } from 'uuid';
import { z } from 'zod';
import { DYNAMIC_SOCKET_BEHAVIORS } from './editorUtils';
import { toAbsoluteStepNodes } from './stepLayoutGroups';

interface MMGraphSocket {
  id: string;
  /**
   * Display label. Held on the entry rather than read from the record key,
   * because the key is the socket id: two sockets can share a display name
   * (a switch case labelled `default` alongside the fixed `default` socket),
   * and keying by name silently collapsed them to one.
   */
  name: string;
  ioType: number;
  socketType?: string;
  required?: boolean;
}

interface NodeDataType extends Record<string, unknown> {
  label: string;
  inputs: {
    [key: string]: MMGraphSocket;
  };
  outputs: {
    [key: string]: MMGraphSocket;
  };
  stepType?: string;
  config?: Record<string, unknown>;
  emits?: string[];
  _stepTypeData?: {
    type: string;
    name: string;
    description: string;
    category: string;
    inputSockets: Array<{
      id: string;
      name: string;
      type: string;
      required?: boolean;
      description?: string;
    }>;
    outputSockets: Array<{
      id: string;
      name: string;
      type: string;
      description?: string;
    }>;
  };
  /** Set by the "refresh step" action so its live sockets are persisted here. */
  __socketsRefreshed?: boolean;
}

type StepDefinition = WorkflowDefinitionResponse['steps'][0];
type EdgeDefinition = WorkflowDefinitionResponse['edges'][0];
type SocketType = StepDefinition['sockets']['inputs'][0]['type'];

function mapNodeInputSockets(
  data: NodeDataType,
  fallbackSockets: StepDefinition['sockets']['inputs'] = [],
): StepDefinition['sockets']['inputs'] {
  const inputSocketMetaById = new Map(
    fallbackSockets.map((socket) => [socket.id, socket]),
  );
  (data._stepTypeData?.inputSockets ?? []).forEach((socket) => {
    inputSocketMetaById.set(socket.id, {
      ...socket,
      required: socket.required ?? true,
    });
  });

  return Object.entries(data.inputs).map(([key, socket]) => ({
    id: socket.id,
    // The label comes off the entry, not the record key: the key is the socket
    // id, and two sockets may share a label.
    name: socket.name ?? key,
    type: (socket.socketType || 'any') as SocketType,
    // Prefer the flag stored on the node socket itself (carried from the
    // persisted definition via mapSockets) — dynamic input sockets are
    // frequently optional (e.g. read-frame column filters), and defaulting
    // to required here would persist a corrupted contract on save.
    required:
      socket.required ?? inputSocketMetaById.get(socket.id)?.required ?? true,
    description: inputSocketMetaById.get(socket.id)?.description,
  }));
}

function mapNodeOutputSockets(
  data: NodeDataType,
  fallbackSockets: StepDefinition['sockets']['outputs'] = [],
): StepDefinition['sockets']['outputs'] {
  const outputSocketMetaById = new Map(
    fallbackSockets.map((socket) => [socket.id, socket]),
  );
  (data._stepTypeData?.outputSockets ?? []).forEach((socket) => {
    outputSocketMetaById.set(socket.id, {
      ...socket,
      required: true,
    });
  });

  return Object.entries(data.outputs).map(([key, socket]) => ({
    id: socket.id,
    name: socket.name ?? key,
    type: (socket.socketType || 'any') as SocketType,
    required: true,
    description: outputSocketMetaById.get(socket.id)?.description,
  }));
}

type StepSockets = StepDefinition['sockets'];
type StepTypeData = NonNullable<NodeDataType['_stepTypeData']>;

function roundedPosition(node: Node): StepDefinition['position'] {
  return { x: Math.round(node.position.x), y: Math.round(node.position.y) };
}

/** A config-dependent step's live sockets, per DYNAMIC_SOCKET_BEHAVIORS. */
function dynamicSockets(
  type: string,
  data: NodeDataType,
  saved: StepSockets,
): StepSockets {
  const behavior = DYNAMIC_SOCKET_BEHAVIORS[type];
  if (!behavior) return saved;
  return {
    inputs: behavior.dynamicInputs
      ? mapNodeInputSockets(data, saved.inputs)
      : saved.inputs,
    outputs: behavior.dynamicOutputs
      ? mapNodeOutputSockets(data, saved.outputs)
      : saved.outputs,
  };
}

/**
 * The sockets an existing step is saved with.
 *
 * For steps with config-dependent sockets, persist the current node sockets so
 * the saved sockets reflect the current config (e.g. switch cases,
 * split_document_v2 classifications, identify_customer customer routes,
 * action_input outputs, workflow_action sockets). Without this, the server
 * keeps whatever sockets were stored on the previous save — and any edges
 * wired to newly-added dynamic sockets quietly disappear on next reload
 * because there's no matching socket id on the persisted node. Driven by the
 * shared DYNAMIC_SOCKET_BEHAVIORS table — keep that in sync with the backend
 * generators in `dynamicOutputSockets.ts`. The "refresh step" action
 * re-derives a static step's sockets from the latest registry definition on
 * the node; persist those live sockets so the refresh survives save/reload
 * (static steps otherwise keep the previously-saved sockets).
 */
function socketsToPersist(
  type: string,
  data: NodeDataType,
  saved: StepSockets,
): StepSockets {
  const sockets =
    data.__socketsRefreshed === true
      ? {
          inputs: mapNodeInputSockets(data, saved.inputs),
          outputs: mapNodeOutputSockets(data, saved.outputs),
        }
      : dynamicSockets(type, data, saved);
  if (type !== 'action_output') return sockets;
  return { inputs: mapNodeInputSockets(data, sockets.inputs), outputs: [] };
}

function existingStepDefinition(
  originalStep: StepDefinition,
  node: Node,
  data: NodeDataType,
): StepDefinition {
  return {
    ...originalStep,
    name: data.label || originalStep.name,
    config: data.config ?? originalStep.config,
    position: roundedPosition(node),
    sockets: socketsToPersist(originalStep.type, data, originalStep.sockets),
  };
}

function newStepDefinition(
  node: Node,
  data: NodeDataType,
  stepTypeData: StepTypeData,
): StepDefinition {
  return {
    id: node.id,
    type: stepTypeData.type,
    name: data.label || stepTypeData.name,
    category: stepTypeData.category as StepDefinition['category'],
    config: data.config ?? {},
    sockets: {
      // Use original socket.id from registry - orchestrator validates against these
      inputs: stepTypeData.inputSockets.map((socket) => ({
        id: socket.id,
        name: socket.name,
        type: (socket.type || 'any') as SocketType,
        required: socket.required ?? true,
        description: socket.description,
      })),
      outputs: stepTypeData.outputSockets.map((socket) => ({
        id: socket.id,
        name: socket.name,
        type: (socket.type || 'any') as SocketType,
        required: true,
        description: socket.description,
      })),
    },
    position: roundedPosition(node),
  };
}

function untypedSockets(
  sockets: NodeDataType['inputs'],
): StepSockets['inputs'] {
  return Object.entries(sockets).map(([key, socket]) => ({
    id: socket.id,
    name: socket.name ?? key,
    type: 'any' as SocketType,
    required: true,
  }));
}

function untypedStepDefinition(node: Node, data: NodeDataType): StepDefinition {
  return {
    id: node.id,
    type: data.stepType || 'unknown',
    name: data.label || 'Unknown Step',
    category: 'Utility' as StepDefinition['category'],
    config: data.config ?? {},
    sockets: {
      inputs: untypedSockets(data.inputs),
      outputs: untypedSockets(data.outputs),
    },
    position: roundedPosition(node),
  };
}

/**
 * Convert React Flow nodes and edges back to workflow definition format.
 *
 * Accepts the raw editor node list, step-layout-group container nodes and all: groups
 * are dropped and their members' positions converted back to workflow-absolute
 * before anything else runs. Doing it here rather than at each of the six call
 * sites means no caller can forget and persist a group as a bogus step, or write
 * group-relative coordinates into `step.position`. Step layout groups themselves are
 * persisted separately — see `extractStepLayoutGroups`.
 */
export function graphToDefinition(
  allNodes: Node[],
  edges: Edge[],
  originalDefinition: WorkflowDefinitionResponse,
): {
  steps: StepDefinition[];
  edges: EdgeDefinition[];
} {
  const nodes = toAbsoluteStepNodes(allNodes);

  const originalStepMap = new Map<string, StepDefinition>();
  originalDefinition.steps.forEach((step) => {
    originalStepMap.set(step.id, step);
  });

  const steps: StepDefinition[] = nodes.map((node) => {
    const data = node.data as NodeDataType;
    const originalStep = originalStepMap.get(node.id);
    if (originalStep) return existingStepDefinition(originalStep, node, data);
    const stepTypeData = data._stepTypeData;
    if (stepTypeData) return newStepDefinition(node, data, stepTypeData);
    // Fallback for nodes without type data - shouldn't normally happen
    return untypedStepDefinition(node, data);
  });

  const nodeSocketMap = new Map<
    string,
    { inputs: Map<string, string>; outputs: Map<string, string> }
  >();
  nodes.forEach((node) => {
    const data = node.data as NodeDataType;
    const inputs = new Map<string, string>();
    const outputs = new Map<string, string>();

    Object.values(data.inputs).forEach((socket) => {
      inputs.set(socket.id, socket.id);
    });
    Object.values(data.outputs).forEach((socket) => {
      outputs.set(socket.id, socket.id);
    });

    nodeSocketMap.set(node.id, { inputs, outputs });
  });

  // Same check the edge contract uses (`z.uuid()`), so a v7 id from a
  // preserve-id import is saved as itself. The old v1–v5 regex minted a new id.
  const persistedEdgeId = z.uuid();

  const definitionEdges: EdgeDefinition[] = edges
    .map((edge) => {
      if (
        !nodes.find((n) => n.id === edge.source) ||
        !nodes.find((n) => n.id === edge.target)
      ) {
        return null;
      }

      const edgeId = persistedEdgeId.safeParse(edge.id).success
        ? edge.id
        : uuidv4();

      return {
        id: edgeId,
        sourceStepId: edge.source,
        sourceSocketId: edge.sourceHandle || '',
        targetStepId: edge.target,
        targetSocketId: edge.targetHandle || '',
      };
    })
    .filter((edge): edge is EdgeDefinition => edge !== null);

  const expressionsByTargetSocket = buildSocketExpressionsFromEdges(
    definitionEdges.filter(
      (edge) => edge.sourceSocketId && edge.targetSocketId,
    ),
  );

  const stepsWithExpressions = steps.map((step) => {
    const manualOverrides = readManualExpressionOverrides(step.config);
    return {
      ...step,
      sockets: {
        ...step.sockets,
        inputs: step.sockets.inputs.map((socket) => {
          const override = manualOverrides[socket.id];
          // Three states, not two — `readManualExpressionOverrides` docs why
          // this distinction matters. A string is an active override; `null`
          // is an explicit clear (this session knows the socket previously
          // had one and is intentionally removing it); an absent key means
          // this session never touched the socket at all, so the save must
          // not claim authority to clear anything the server may already
          // have — the backend's mapStepInputToModel preserves in that case.
          if (typeof override === 'string') {
            return {
              ...socket,
              expression: override,
              // Execution must always honor a manual override regardless of
              // its content (see resolveStepInputsWithDualRead) — it has no
              // corresponding edge to fall back to, unlike auto-derived
              // output/collect expressions.
              manualOverride: true,
            };
          }
          if (override === null) {
            return {
              ...socket,
              expression: expressionsByTargetSocket.get(
                `${step.id}:${socket.id}`,
              ),
              manualOverride: false,
            };
          }
          return {
            ...socket,
            expression: expressionsByTargetSocket.get(
              `${step.id}:${socket.id}`,
            ),
            manualOverride: undefined,
          };
        }),
      },
    };
  });

  return { steps: stepsWithExpressions, edges: definitionEdges };
}
